<template>
    <div class="container mt-3 mb-5">
        <p>You can explore SCKAN knowledge base by asking questions such as;</p>
        <div class="container mt-3 mb-2 qbox">
            <ul class="no-bullets list-group">
                <!--
                <li>Is there a connection from inferior mesenteric ganglion to the urinary bladder in rats? Summarize
                    the
                    pathways based on the nerves involved.</li>
                <li>To what organs does the pelvic ganglion project? Summarize the connections categorized by end
                    organs.
                    Only list the end organs.</li>
                <li> What connections originate the nucleus of brain? Categorize the pathways based on different brain
                    nucleus.</li>
                <li>What connections terminate in the urinary bladder? Concisely summarize the pathways categorized as
                    follows: What are the origins of those connections? What are the exact parts of the organ the
                    connections terminate? What nerves are involved in those connections?</li>
                <li>What organs are innervated by vagus nerve? Summarize the pathways categorized by the origins and the
                    end
                    organ systems.</li>
                -->
                <li>Track the sympathetic pathways to the ovaries. Show the origins, terminations, and the routings for
                    both
                    pre-ganglionic and post-ganglionic connections. Summarize the results. (Please have ‘Include
                    synaptic paths’ checked)</li>
                 <!--  
                <li>Track the parasympathetic pathways to the kidney. (Please have ‘Include synaptic paths’ checked)
                </li>
                -->
                <li>What anatomical structures can be stimulated by inferior mesenteric ganglion? Only list unique
                    origins, destinations, and via structures.</li>
                <li>What connections terminate in the urinary bladder? Concisely summarize the pathways. 
                    What nerves are involved in those connections?</li>
            </ul>
        </div>
        <div class="container mb-5">
            <p>For a more comprehensive introduction to the system, please check
                <a class="link-info" target="_blank"
                    href="https://github.com/smtifahim/SCKAN-Apps/blob/master/sckan-explorer/json/sckan-nli/sckan-nli-intro.md">this
                    document</a>.
            </p>
        </div>
    </div>
    <div class="container mt-3">
    
        <div class="row">
            <div class="col-11 text-start mb-2">
                Tip: The first part of your question must indicate at least one aspect of the connectivity pathway, 
                such as the origin, the destination, or the via. You can then add instructions on how you want the answer summarized.
            </div>
        </div>
        <div class="row">
            <div class="col-11">
                <AutoComplete v-model="question" ph="Ask your question to SCKAN KB" :items="items" />
                <div id="syn-div">
                    <input type="checkbox" id="synapse-cb" v-model="synaptic">
                    <label for="synapse-cb" class="syn-label">Include synaptic paths</label>
                </div>

            </div>
            <div class="col-1">
                <!-- <button class="btn btn-primary" @click.prevent="executeRecaptcha">Ask</button> -->
                <button class="btn btn-primary" @click.prevent="handleAsk">Ask</button>
            </div>

        </div>
        <div class="d-flex justify-content-center m-3" v-if="loading">
            <div class="spinner-border text-info" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>
        <div v-if="hasResults" class="container mt-3">
            <AnswerPanel v-if="hasAnswer" :answer="answer" />
            <div class="row">
                <div class="col m-3">
                    <button class="btn btn-primary btn-sm mx-3" @click.prevent="toggleExplain">Explain</button>
                    <button class="btn btn-primary btn-sm" @click.prevent="togglePanel">Provide Feedback</button>
                </div>
            </div>
            <ExplainPanel :qt="qt" v-show="explainVisible" />
            <div class="alert alert-danger" v-if="feedbackResponse.status === 'error'">{{ feedbackResponse.msg }}</div>
            <div class="alert alert-success" v-if="feedbackResponse.status === 'success'">{{ feedbackResponse.msg }}
            </div>

            <FeedbackPanel :question="question" :semFrame="semFrame" v-show="feedbackVisible"
                @feedbackStatus="handleFeedbackStatus" />
            <CompositeVizPanel :compositeDg="compositeSymDG" :compositeSynapseDg="compositeSynapseSymDG"
                title="Sympathetic Paths Only" v-if="hasCompositeSymGraph" />
            <CompositeVizPanel :compositeDg="compositeParaDG" :compositeSynapseDg="compositeSynapseParaDG"
                title="Parasympathetic Paths Only" v-if="hasCompositeParaGraph" />
            <AggregateVizPanel :aggregateDg="aggregateDG" v-if="hasAggregateDG"/>
            <NeuronPopulationTable v-for="(np, idx) in neuronData" :neuronID="np['neuron_id']"
                :neuronData="np['neuron_data']" :key="idx" />
            <!-- <pre>{{ neuronData }}</pre> -->
        </div>
        <div v-if="hasNoQueryResults" class="container mt-3">
            <AnswerPanel v-if="hasAnswer" :answer="answer" />
            <div class="row">
                <div class="col m-3">
                    <button class="btn btn-primary btn-sm mx-3" @click.prevent="toggleExplain">Explain</button>
                    <button class="btn btn-primary btn-sm" @click.prevent="togglePanel">Provide Feedback</button>
                </div>
            </div>
            <ExplainPanel :qt="qt" v-show="explainVisible" />
            <div class="alert alert-danger" v-if="feedbackResponse.status === 'error'">{{ feedbackResponse.msg }}</div>
            <div class="alert alert-success" v-if="feedbackResponse.status === 'success'">{{ feedbackResponse.msg }}
            </div>

            <FeedbackPanel :question="question" :semFrame="semFrame" v-show="feedbackVisible"
                @feedbackStatus="handleFeedbackStatus" />
            <p>No results returned from SCKAN Knowledge Base for your question.</p>
        </div>
    </div>
    <FooterPanel />
</template>

<script>
import AutoComplete from './AutoComplete.vue';
import NeuronPopulationTable from './NeuronPopulationTable.vue';
import FeedbackPanel from './FeedbackPanel.vue';
import AnswerPanel from './AnswerPanel.vue';
import ExplainPanel from './ExplainPanel.vue';
import FooterPanel from './FooterPanel.vue';
import CompositeVizPanel from './CompositeVizPanel.vue';
import AggregateVizPanel from './AggregateVizPanel.vue';
import axios from 'axios';

export default {
    inject: {
        siteKey: {
            from: 'grc_site_key'
        }
    },
    data() {
        return {
            question: "",
            neuronData: [],
            resultsReturned: false,
            items: [],
            semFrame: '',
            feedbackVisible: false,
            feedbackResponse: { msg: '', status: '' },
            answer: '',
            qt: null,
            explainVisible: false,
            loading: false,
            synaptic: false,
            compositeSymDG: '',
            compositeSynapseSymDG: '',
            compositeParaDG: '',
            compositeSynapseParaDG: '',
            aggregateDG: ''
        }
    },
    // props: ['neuronData'],
    components: {
        NeuronPopulationTable, AutoComplete, FeedbackPanel,
        AnswerPanel, ExplainPanel, FooterPanel, CompositeVizPanel, 
        AggregateVizPanel
    },
    computed: {
        hasResults() {
            return this.resultsReturned && this.neuronData && this.neuronData.length > 0;
        },
        hasNoQueryResults() {
            return this.resultsReturned && (!this.neuronData || this.neuronData.length === 0);
        },
        hasAnswer() {
            return this.answer !== '' && this.answer.trim().length > 0;
        },
        hasCompositeSymGraph() {
            return this.compositeSymDG !== '';
        },
        hasCompositeParaGraph() {
            return this.compositeParaDG !== '';
        },
        hasAggregateDG() {
            return this.aggregateDG !== '';
        }
    },
    methods: {
        // send your recaptcha token to the server to verify it
        submit(response) {
            // console.log(response);
            const that = this;
            axios.post('/llm_api/captcha_verify', { response: response })
                .then(function (verify_response) {
                    // console.log('verify response:' + verify_response);
                    console.log("success:" + verify_response.data.success);
                    if (verify_response.data.success) {
                        console.log("calling handleAsk");
                        that.handleAsk();
                    }
                }).catch(function (err) {
                    console.log(err);
                });
        },
        // execute the recaptcha widget
        executeRecaptcha() {
            // this.$refs.recaptcha.execute()
            const that = this;

            const grecaptcha = window.grecaptcha;
            // console.log('grecaptcha:' + grecaptcha);       
            const siteKey = this.siteKey;
            // console.log("siteKey:" + siteKey);

            grecaptcha.ready(function () {
                grecaptcha.execute(siteKey)
                    .then(function (token) {
                        // console.log('token:' + token);
                        that.submit(token);
                    });
            });

        },
        handleAsk() {
            this.resultsReturned = false;
            console.log("question:" + this.question);
            const that = this;
            this.loading = true;
            axios.post('/llm_api/answer', { question: this.question, synaptic: this.synaptic })
                .then(function (response) {
                    console.log(response);
                    that.loading = false;
                    if ('qt' in response.data) {
                        that.qt = response.data['qt'];
                        that.semFrame = JSON.stringify(response.data['qt']);
                        that.neuronData = response.data['ng_list'];
                        that.answer = response.data['answer'];
                        that.resultsReturned = true;
                        that.feedbackResponse.status = '';
                        that.compositeSymDG = response.data['composite_sym_dg'];
                        that.compositeSynapseSymDG = response.data["composite_synapse_sym_dg"];
                        that.compositeParaDG = response.data["composite_para_dg"];
                        that.compositeSynapseParaDG = response.data["composite_synapse_para_dg"];
                        that.aggregateDG = response.data["aggregate_dg"];
                        // console.log(">> compositeDg:" + that.compositeDG);
                    }
                }).catch(function (err) {
                    that.loading = false;
                    console.log(err);
                });

        },
        handleClick() {
            this.resultsReturned = false;
            console.log("question:" + this.question);
            const that = this;
            // axios.post('http://127.0.0.1:7081/extract_template_from_question',
            axios.post('/llm_api/extract_template_from_question',
                { question: this.question })
                .then(function (response) {
                    console.log(response)
                    const qt = response.data;
                    that.semFrame = JSON.stringify(response.data);

                    // axios.post('http://127.0.0.1:8000/sckan_kb_search',
                    axios.post('/explorer_api/sckan_kb_search',
                        qt)
                        .then(function (response) {
                            console.log(response)
                            that.neuronData = response.data;
                            that.resultsReturned = true;
                            that.feedbackResponse.status = '';
                        })
                        .catch(function (err) {
                            console.log(err);
                        })
                })
                .catch(function (err) {
                    console.log(err)
                });
        },
        togglePanel() {
            this.feedbackVisible = !this.feedbackVisible;
        },
        toggleExplain() {
            this.explainVisible = !this.explainVisible;
        },
        handleFeedbackStatus(response) {
            const status = response.status;
            if (status === 'ok') {
                this.feedbackResponse.msg = 'Your feedback is successfully saved!'
                this.feedbackResponse.status = 'success';
                this.feedbackVisible = false;
            } else {
                this.feedbackResponse.msg = status;
                this.feedbackResponse.status = 'error'
            }
        }

    },
    mounted() {
        const that = this;
        // axios.get('http://127.0.0.1:8000/autocomplete_list')
        axios.get('/explorer_api/autocomplete_list')
            .then(function (response) {
                // console.log(response)
                that.items = response.data;
            })
            .catch(function (err) {
                console.log(err);
                that.items = []
            });
    },
}

</script>

<style scoped>
ul.no-bullets {
    list-style-type: none;
    /* Remove bullets */
    padding: 0;
    /* Remove padding */
    margin: 0;
    /* Remove margins */
}

ul.no-bullets li {
    margin-bottom: 4px;
    padding: 0;
    /* border: 1px solid black; */
}

#syn-div {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 10px auto 20px auto;
    width: fit-content;
}

#synapse-cb {
    width: 20px;
    height: 20px;
    vertical-align: middle;
}

.syn-label {
    background-color: white;
    color: black;
    padding: 5px 5px;
    cursor: pointer;
    margin-left: 3px;
    vertical-align: middle;
}
.qbox {
    background-color: #F5F5F5;
}

#orcidWidget{
  padding: 1em;
  margin: 1em auto;
  /* background-color: pink; */
}
</style>